import { useEffect, useMemo } from 'react';
import { BufferAttribute, BufferGeometry, CanvasTexture, RepeatWrapping, SRGBColorSpace } from 'three';

/**
 * 주행 경계의 커브(적백 블록).
 *
 * 아스팔트와 흰 선만으로는 "넓은 포장 바닥"까지입니다. 트랙이라고 읽히게 하는 결정적인
 * 요소가 이 적백 블록입니다. 경계가 원이라 실제로는 스키드패드(정지 선회 시험장)의
 * 모양이 되는데, 그게 지금 물리 경계와 정확히 일치하는 형태라 거짓말을 하지 않습니다.
 *
 * 왜 RingGeometry를 쓰지 않는가:
 *   three의 RingGeometry UV는 평면 투영입니다(uv.x = (x/r + 1) / 2). 원주를 따라 블록을
 *   반복시킬 수가 없습니다. 그래서 극좌표 UV를 가진 링을 직접 만듭니다 — u는 각도,
 *   v는 반경 방향입니다. 그러면 2칸짜리 작은 텍스처를 repeat로 늘려 쓸 수 있어서
 *   큰 텍스처를 만들 필요가 없습니다.
 */

/** 블록 한 칸의 목표 길이 (m). 실제 트랙 커브와 비슷한 크기입니다. */
const BLOCK_LENGTH = 2;

const KERB_RED = '#b3282d';
const KERB_WHITE = '#e8e8ea';
/** 텍셀 크기. 밉맵이 의미 있을 만큼은 되어야 멀리서 지글거리지 않습니다. */
const TEXTURE_WIDTH = 64;
const TEXTURE_HEIGHT = 16;

/**
 * 극좌표 UV를 가진 링.
 *
 * XY 평면에 만듭니다. 노면과 같은 -90° 회전을 그대로 쓸 수 있습니다.
 */
function createPolarRing(innerRadius: number, outerRadius: number, segments: number): BufferGeometry {
  const positions = new Float32Array((segments + 1) * 2 * 3);
  const uvs = new Float32Array((segments + 1) * 2 * 2);
  const indices: number[] = [];

  for (let i = 0; i <= segments; i += 1) {
    const fraction = i / segments;
    const angle = fraction * Math.PI * 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    [innerRadius, outerRadius].forEach((radius, edge) => {
      const vertex = (i * 2 + edge) * 3;
      positions[vertex] = cos * radius;
      positions[vertex + 1] = sin * radius;
      positions[vertex + 2] = 0;
      const uv = (i * 2 + edge) * 2;
      // u는 원주를 한 바퀴 도는 0~1, v는 링을 가로지르는 0~1입니다.
      uvs[uv] = fraction;
      uvs[uv + 1] = edge;
    });

    if (i < segments) {
      const base = i * 2;
      indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

/** 적백 두 칸. repeat으로 원주에 늘립니다. */
function createKerbTexture(): CanvasTexture | null {
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_WIDTH;
  canvas.height = TEXTURE_HEIGHT;
  const context = canvas.getContext('2d');
  if (context === null) {
    return null;
  }
  context.fillStyle = KERB_WHITE;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = KERB_RED;
  context.fillRect(0, 0, canvas.width / 2, canvas.height);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  // u만 반복합니다. v는 링을 한 번 가로지르므로 늘리면 블록이 겹칩니다.
  texture.wrapS = RepeatWrapping;
  return texture;
}

export interface TrackKerbProps {
  /** 커브 안쪽 반경 (m). 보통 주행 경계와 같습니다. */
  innerRadius: number;
  /** 커브 폭 (m). */
  width: number;
  y: number;
  anisotropy: number;
}

export function TrackKerb({ innerRadius, width, y, anisotropy }: TrackKerbProps) {
  const outerRadius = innerRadius + width;
  /*
   * 블록 수는 **짝수**여야 합니다. 텍스처가 적·백 두 칸이라 repeat이 blocks/2인데,
   * 홀수면 반복 횟수가 정수가 아니어서 각도 0에서 무늬가 어긋납니다(빨강이 빨강과
   * 맞닿는 이음선이 한 줄 생깁니다).
   */
  const blocks = 2 * Math.max(4, Math.round((2 * Math.PI * innerRadius) / BLOCK_LENGTH / 2));
  const geometry = useMemo(() => createPolarRing(innerRadius, outerRadius, blocks * 4), [
    innerRadius,
    outerRadius,
    blocks,
  ]);
  const texture = useMemo(() => createKerbTexture(), []);

  useEffect(() => () => geometry.dispose(), [geometry]);
  useEffect(() => () => texture?.dispose(), [texture]);

  useMemo(() => {
    if (texture === null) {
      return;
    }
    // 텍스처가 두 칸이라 블록 수의 절반을 반복합니다.
    texture.repeat.set(blocks / 2, 1);
    texture.anisotropy = anisotropy;
    texture.needsUpdate = true;
  }, [texture, blocks, anisotropy]);

  if (texture === null) {
    return null;
  }

  return (
    <mesh geometry={geometry} rotation={[-Math.PI / 2, 0, 0]} position={[0, y, 0]}>
      {/* 칠한 것이라 조명을 받습니다. 배경이 바뀔 때 노면과 함께 밝아져야 같은 면으로 보입니다. */}
      <meshStandardMaterial map={texture} roughness={0.55} metalness={0} />
    </mesh>
  );
}
